import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_AUTO_SAVE_DELAY_MS = 30000;

interface UsePropertyAutoSaveParams {
  enabled: boolean;
  save: () => Promise<void>;
  delayMs?: number;
}

interface UsePropertyAutoSaveResult {
  isAutoSaving: boolean;
  lastSavedAt: Date | null;
  hasUnsavedChanges: boolean;
  hasError: boolean;
  markChanged: () => void;
  saveNow: () => Promise<void>;
}

/**
 * Debounced auto-save for the property draft form. `markChanged` schedules a
 * background save after a period of inactivity, while `saveNow` flushes
 * immediately for explicit user actions (e.g. the "Save draft" button).
 */
export function usePropertyAutoSave({
  enabled,
  save,
  delayMs = DEFAULT_AUTO_SAVE_DELAY_MS,
}: UsePropertyAutoSaveParams): UsePropertyAutoSaveResult {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasError, setHasError] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  const saveRef = useRef(save);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runSave = useCallback(async () => {
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    setIsAutoSaving(true);
    setHasError(false);

    try {
      await saveRef.current();
      setLastSavedAt(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      setHasError(true);
      throw error;
    } finally {
      isSavingRef.current = false;
      setIsAutoSaving(false);
    }
  }, []);

  const markChanged = useCallback(() => {
    setHasUnsavedChanges(true);
    if (!enabledRef.current) return;

    clearTimer();
    timerRef.current = setTimeout(() => {
      runSave().catch(() => undefined);
    }, delayMs);
  }, [clearTimer, delayMs, runSave]);

  const saveNow = useCallback(async () => {
    clearTimer();
    await runSave();
  }, [clearTimer, runSave]);

  useEffect(() => clearTimer, [clearTimer]);

  return {
    isAutoSaving,
    lastSavedAt,
    hasUnsavedChanges,
    hasError,
    markChanged,
    saveNow,
  };
}
